<template>
  <div class="d-flex flex-column ga-4">
    <div v-for="row in visibleRows" :key="row.id" class="ai-suggestion-row">
      <template v-if="row.state === 'added'">
        <div class="text-body-2">
          <v-icon size="small" color="primary">mdi-check</v-icon>
          {{ row.description }}
        </div>
        <div class="text-caption text-medium-emphasis">
          Wordt aangemaakt als je het rapport opslaat.
        </div>
      </template>

      <template v-else>
        <v-textarea
          :model-value="row.description"
          label="Taak"
          variant="outlined"
          density="comfortable"
          rows="2"
          auto-grow
          hide-details
          class="mb-2"
          @update:model-value="update(row, { description: $event })"
        />
        <div class="d-flex flex-wrap ga-2">
          <v-text-field
            :model-value="row.deadline"
            label="Datum"
            type="date"
            variant="outlined"
            density="comfortable"
            hide-details
            class="ai-follow-up__field"
            @update:model-value="update(row, { deadline: $event })"
          />
          <v-select
            :model-value="row.deadline_category_id"
            :items="categories"
            item-title="name"
            item-value="id"
            label="Categorie"
            variant="outlined"
            density="comfortable"
            hide-details
            class="ai-follow-up__field"
            @update:model-value="update(row, { deadline_category_id: $event })"
          />
          <v-select
            :model-value="row.user_id"
            :items="assignees"
            item-title="name"
            item-value="id"
            label="Voor"
            variant="outlined"
            density="comfortable"
            hide-details
            class="ai-follow-up__field"
            @update:model-value="update(row, { user_id: $event })"
          />
        </div>
        <div class="d-flex ga-2 mt-2 ai-suggestion-actions">
          <v-btn
            color="primary"
            variant="flat"
            class="flex-grow-1"
            prepend-icon="mdi-plus"
            :disabled="!isComplete(row)"
            @click="$emit('add', row.id)"
          >
            Toevoegen
          </v-btn>
          <v-btn variant="text" @click="$emit('reject', row.id)"
            >Afwijzen</v-btn
          >
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
/**
 * The tasks section of the AI-check. Each suggested task is a proposal the worker
 * can edit and must explicitly add; added tasks are only created after the report is
 * saved.
 */
import { computed, onMounted } from "vue";
import { useDeadlineCategoriesStoreRefactor } from "@/stores/deadlineCategoriesStoreRefactor";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";

const props = defineProps({
  // [{ id, description, deadline, deadline_category_id, user_id, state }]
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(["update", "add", "reject"]);

const categoriesStore = useDeadlineCategoriesStoreRefactor();
const usersStore = useUsersStoreRefactor();

const visibleRows = computed(() =>
  props.rows.filter((row) => row.state !== "rejected"),
);
const categories = computed(() => categoriesStore.categories || []);
// null = a task for everyone, as in the task form
const assignees = computed(() => [
  { id: null, name: "Iedereen" },
  ...(usersStore.activeUsersInCurrentTenant || []),
]);

const update = (row, patch) => emit("update", row.id, patch);
const isComplete = (row) =>
  !!row.description?.trim() && !!row.deadline && !!row.deadline_category_id;

onMounted(() => {
  if (!categories.value.length) categoriesStore.fetchAll().catch(() => {});
  if (!usersStore.users?.length) usersStore.fetchAll().catch(() => {});
});
</script>

<style scoped>
.ai-follow-up__field {
  flex: 1 1 140px;
  min-width: 140px;
}
</style>
